export interface Sector {
  slug: string
  name: string
  shortDescription: string
  overview: string
  procurementRequirements: string[]
  relevantCategories: string[]
  relevantCapabilities: string[]
  image: string
  useCases: string
  typicalCategories: { name: string; slug: string }[]
  documentationNote: string
  frameworkNote: string
}

export const sectors: Sector[] = [
  {
    slug: 'defence',
    name: 'Defence',
    shortDescription:
      'Specialist equipment manufacturing for armed forces and defence contractors.',
    overview:
      'Chiltern Global has a long-standing history of supplying defence organisations with mission-critical equipment, working directly with procurement teams, prime contractors, and logistics commands. Our manufacturing processes are aligned with the rigorous demands of modern military operations, ensuring durability, functionality, and compliance with strict technical specifications including DEF STAN requirements.\n\nWe support the full procurement lifecycle, from initial specification development and prototype evaluation through to volume production and in-service support. Our supply chain is JOSCAR-registered and operates to ISO 9001:2015, providing the audit trail and traceability required by defence procurement authorities.\n\nBuyers can procure through approved framework agreements or direct contract routes. Our team is experienced in working within MOD procurement processes, supporting both standard-issue programmes and urgent operational requirements.',
    procurementRequirements: [
      'Compliance with DEF STAN specifications',
      'JOSCAR-registered supply chain',
      'ISO 9001:2015 Quality Management',
      'Cyber Essentials Plus certification',
      'NATO Stock Number (NSN) codification capability',
      'Secure supply chain management',
    ],
    relevantCategories: [
      'bags-backpacks',
      'clothing',
      'tactical-vests',
      'training-equipment',
    ],
    relevantCapabilities: [
      'research-design-development',
      'manufacturing-production',
      'quality-assurance',
    ],
    image:
      'https://images.unsplash.com/photo-1595590424283-b8f17842773f?w=800&q=80',
    useCases:
      'Chiltern Global equipment is deployed across infantry, logistics, and support roles within UK and allied armed forces. Products are used in training environments, operational deployments, and base logistics, with configurations tailored to unit-specific requirements. Our manufacturing capability supports both standard-issue programmes and bespoke operational requirements, including urgent operational requirements (UOR) where accelerated delivery timescales apply.',
    typicalCategories: [
      { name: 'Tactical Vests', slug: 'tactical-vests' },
      { name: 'Bags and Backpacks', slug: 'bags-backpacks' },
      { name: 'Clothing', slug: 'clothing' },
      { name: 'Training Equipment', slug: 'training-equipment' },
    ],
    documentationNote:
      'Technical datasheets, material compliance records, test reports, and Certificates of Conformity are available on request for all defence-grade products. Documentation is aligned to DEF STAN specification requirements and can be provided to support procurement evaluation processes, contract award, and in-service review.',
    frameworkNote:
      'Defence procurement may be conducted through approved framework agreements, providing compliant and auditable routes to market. Chiltern Global can support buyers in identifying the most appropriate procurement route for their requirement, including framework call-off and direct award options.',
  },
  {
    slug: 'police',
    name: 'Police',
    shortDescription:
      'Public order and patrol equipment for law enforcement agencies.',
    overview:
      'We support police forces across the UK with a comprehensive range of operational equipment, addressing the specific procurement requirements of uniformed, tactical, and specialist policing functions. Our products are designed to meet Home Office technical standards and force-specific specifications, balancing officer protection with operational mobility and comfort.\n\nFrom protective limb guards and public order shields to high-visibility tactical vests and load-carrying systems, our manufacturing capabilities ensure consistent quality and reliability across volume supply contracts. We understand the procurement frameworks used by UK policing, including BlueLight Commercial, and are experienced in delivering to force evaluation panel requirements.\n\nWe support forces through the full procurement process, from initial market engagement and sample evaluation through to contract delivery and after-sales support. Our team can provide technical input to specification development and assist with user trial documentation.',
    procurementRequirements: [
      'Home Office technical standards compliance',
      'BS 7971 protective clothing standards',
      'Ethical trading audits',
      'Sample evaluation and user trials',
      'Just-in-time delivery options',
      'Warranty and after-sales support',
    ],
    relevantCategories: [
      'public-order',
      'tactical-vests',
      'clothing',
      'bags-backpacks',
    ],
    relevantCapabilities: [
      'manufacturing-production',
      'logistics-delivery',
      'research-design-development',
    ],
    image:
      'https://images.unsplash.com/photo-1551884831-bbf3ddd3686a?w=800&q=80',
    useCases:
      'Equipment supplied to police forces is used across patrol, public order, and specialist operations. Products are configured to force-specific requirements and are suitable for use in both routine duties and planned operations. Our range supports officers across uniformed, tactical, and support functions, including firearms units, public order serials, dog sections, and roads policing.',
    typicalCategories: [
      { name: 'Public Order', slug: 'public-order' },
      { name: 'Tactical Vests', slug: 'tactical-vests' },
      { name: 'Clothing', slug: 'clothing' },
      { name: 'Bags and Backpacks', slug: 'bags-backpacks' },
    ],
    documentationNote:
      'Product documentation including technical specifications, sizing guides, compliance records, and care instructions is available on request. Documentation can be provided to support force evaluation panels, procurement approval processes, and officer safety committee reviews.',
    frameworkNote:
      'Police procurement is frequently conducted through BlueLight Commercial and other approved frameworks. Chiltern Global is positioned to supply through compliant routes, reducing procurement timescales and administrative burden for buying organisations. Our team can advise on applicable lots and call-off procedures.',
  },
  {
    slug: 'prisons',
    name: 'Prisons',
    shortDescription:
      'Control and restraint equipment for custodial environments.',
    overview:
      'Chiltern Global manufactures specialised equipment for the custodial sector, addressing the unique operational and procurement requirements of prison services, immigration removal centres, and secure facilities. Our product range covers control and restraint equipment, officer protection, fire-retardant materials, and training equipment, all designed for the demands of secure environments.\n\nWe work with prison services and procurement teams to develop and supply products that meet Ministry of Justice technical specifications and HMPPS operational requirements. Our manufacturing processes prioritise robust construction, tamper-resistant design, and material compliance, ensuring longevity and reliability in high-use custodial settings.\n\nProcurement can be routed through approved public sector frameworks or direct contract, with full documentation and traceability provided to support internal approval processes and audit requirements.',
    procurementRequirements: [
      'Ministry of Justice technical specifications',
      'Control and Restraint (C&R) standards',
      'Fire retardancy certification',
      'Anti-ligature design considerations',
      'Bulk supply capability',
      'Traceability and batch testing',
    ],
    relevantCategories: [
      'public-order',
      'clothing',
      'fire-retardants',
      'training-equipment',
    ],
    relevantCapabilities: [
      'manufacturing-production',
      'quality-assurance',
      'logistics-delivery',
    ],
    image:
      'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?w=800&q=80',
    useCases:
      'Products supplied to custodial services are used in control and restraint operations, cell entry procedures, staff training, and routine officer duties. Equipment is selected and configured to meet the specific demands of secure environments, with durability, tamper-resistance, and fire retardancy as primary considerations. Our range supports both operational staff and training functions across the custodial estate.',
    typicalCategories: [
      { name: 'Public Order', slug: 'public-order' },
      { name: 'Clothing', slug: 'clothing' },
      { name: 'Fire Retardants', slug: 'fire-retardants' },
      { name: 'Training Equipment', slug: 'training-equipment' },
    ],
    documentationNote:
      'Documentation including fire retardancy certificates, material safety data, product specifications, and batch test records is available on request. All documentation is provided to support Ministry of Justice procurement processes, HMPPS approval requirements, and internal governance reviews.',
    frameworkNote:
      'Custodial procurement may be routed through approved public sector frameworks, providing compliant and cost-effective access to Chiltern Global products. Our team can advise on the most appropriate procurement route for your organisation and assist with framework call-off documentation.',
  },
  {
    slug: 'medical',
    name: 'Medical',
    shortDescription:
      'Textile manufacturing for healthcare and emergency services.',
    overview:
      'Our medical division specialises in the manufacture of textile-based medical equipment, protective wear, and patient handling products for healthcare providers, ambulance services, and NGO medical teams. We supply organisations operating in both domestic NHS settings and international humanitarian environments, with products designed to meet infection control requirements and clinical operational standards.\n\nCapabilities include the production of patient transfer systems, specialist medical bags, protective garments, and fire-retardant materials for healthcare use. We operate to strict hygiene and quality standards, with quality management processes aligned to ISO 13485 requirements where applicable.\n\nProcurement can be conducted through NHS Supply Chain frameworks and other approved public sector routes. Our team can support buyers in identifying compliant procurement pathways and provide the documentation required for NHS procurement approval processes.',
    procurementRequirements: [
      'Medical Device Regulation (MDR) compliance',
      'ISO 13485 quality management alignment',
      'Infection control material standards',
      'NHS supply chain framework alignment',
      'Rapid prototype development',
      'Sterile manufacturing capability (where applicable)',
    ],
    relevantCategories: ['bags-backpacks', 'clothing', 'fire-retardants'],
    relevantCapabilities: [
      'research-design-development',
      'quality-assurance',
      'manufacturing-production',
    ],
    image:
      'https://images.unsplash.com/photo-1584036561566-b93a9499cb6d?w=800&q=80',
    useCases:
      'Textile-based medical equipment supplied by Chiltern Global is used in pre-hospital care, patient transfer, and infection control environments. Products are used by NHS trusts, ambulance services, and NGO medical teams operating in both domestic and international settings. Our range supports clinical, logistics, and field medical functions across a range of healthcare procurement categories.',
    typicalCategories: [
      { name: 'Bags and Backpacks', slug: 'bags-backpacks' },
      { name: 'Clothing', slug: 'clothing' },
      { name: 'Fire Retardants', slug: 'fire-retardants' },
    ],
    documentationNote:
      'Product documentation including material compliance records, infection control data, technical specifications, and quality certificates is available on request. Documentation is aligned to NHS supply chain and regulatory requirements where applicable, and can be provided to support procurement evaluation and clinical approval processes.',
    frameworkNote:
      'Medical and healthcare procurement may be conducted through NHS Supply Chain frameworks and other approved public sector routes. Chiltern Global can support buyers in identifying compliant procurement pathways for their specific requirements and provide the documentation needed for NHS procurement approval.',
  },
]
